import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Target, Calendar, Inbox, Check, ChevronRight } from 'lucide-react';
import { T, ROLES, springs } from '../../lib/tokens';
import { Glass } from '../glass/Glass';
import { RoleBadge } from '../primitives/Badge';
import { Btn } from '../primitives/Btn';
import { fmtMinutes } from '../../lib/helpers';
import { supabase } from '../../lib/supabase';
import { DeferMenu } from './DeferMenu';

const colorMap = {
  red: { accent: T.red, bg: 'rgba(224,88,88,0.06)', border: 'rgba(224,88,88,0.20)' },
  amber: { accent: T.amber, bg: 'rgba(212,146,30,0.06)', border: 'rgba(212,146,30,0.20)' },
  blue: { accent: T.blue, bg: 'rgba(74,158,204,0.06)', border: 'rgba(74,158,204,0.20)' },
  violet: { accent: T.violet, bg: 'rgba(139,114,200,0.06)', border: 'rgba(139,114,200,0.20)' },
};

const typeIcons = {
  task: Zap,
  goal_alert: Target,
  meeting_prep: Calendar,
  inbox_unread: Inbox,
};

export const NowCard = ({ priorityItem, onUpdate, onNavigate }) => {
  if (!priorityItem) return null;

  const { type, item, reason, color, roleSlug } = priorityItem;
  const colors = colorMap[color] || colorMap.amber;
  const Icon = typeIcons[type] || Zap;

  const getTitle = () => {
    switch (type) {
      case 'task': return item.title;
      case 'goal_alert': return item.title;
      case 'meeting_prep': return `Prep: ${item.desc?.replace(/\s*\[.*?\]/, '').replace(/\s*—.*/, '')}`;
      case 'inbox_unread': return `${item.count} unread brief${item.count > 1 ? 's' : ''}`;
      default: return '';
    }
  };

  const getSubtitle = () => {
    switch (type) {
      case 'task':
        return [
          item.effort && fmtMinutes(typeof item.effortMin === 'number' ? item.effortMin : 60),
          item.goals?.title,
        ].filter(Boolean).join(' · ');
      case 'goal_alert': return reason;
      case 'meeting_prep': return reason;
      case 'inbox_unread': return 'Review your latest intelligence';
      default: return reason;
    }
  };

  const handleDone = async () => {
    if (type === 'task') {
      await supabase.from('tasks').update({ done: true, done_at: new Date().toISOString() }).eq('id', item.id);
      onUpdate();
    } else if (type === 'inbox_unread') {
      onNavigate('inbox');
    } else if (type === 'goal_alert') {
      onNavigate('goals');
    }
  };

  const getAction = () => {
    switch (type) {
      case 'task': return 'Done';
      case 'goal_alert': return 'Review';
      case 'meeting_prep': return 'View';
      case 'inbox_unread': return 'Read';
      default: return 'Open';
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={item?.id || type}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={springs.gentle}
      >
        <Glass
          depth={2}
          tint={color === 'red' ? 'red' : color === 'amber' ? 'amber' : undefined}
          hover={false}
          style={{
            padding: '20px 24px',
            borderLeft: `3px solid ${colors.accent}`,
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Icon size={12} color={colors.accent} />
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: colors.accent,
            }}>
              {type === 'task' ? 'Focus now' : type === 'goal_alert' ? 'Needs attention' : type === 'meeting_prep' ? 'Prepare' : 'Review'}
            </span>
            <div style={{ flex: 1 }} />
            {roleSlug && <RoleBadge roleSlug={roleSlug} small />}
          </div>

          {/* Title */}
          <div style={{ fontSize: 17, fontWeight: 600, color: T.text, lineHeight: 1.3, marginBottom: 8, letterSpacing: '-0.02em' }}>
            {getTitle()}
          </div>

          {/* Subtitle */}
          {getSubtitle() && (
            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 14 }}>
              {getSubtitle()}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn variant="primary" small onClick={handleDone}>
              <Check size={12} /> {getAction()}
            </Btn>
            {type === 'task' && (
              <DeferMenu task={item} label="Defer" align="left" onDeferred={() => onUpdate()} />
            )}
          </div>
        </Glass>
      </motion.div>
    </AnimatePresence>
  );
};
